import React from 'react'
import { Stack, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'

import { BEIGE_500, BEIGE_600, BEIGE_700, DARK_ORANGE, GRAY_500, GRAY_600, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { MUITooltip } from 'components/common/MUITooltip'
import { formatPrice } from 'utils/price/priceFormatter'

import { StruckPrice } from '../StruckPrice.component'
import { pressableFocusSx, pressableProps } from '../pressableProps'
import type { CartDisplayRow } from './useCartDisplay'
import { MUIButton } from 'components/common/MUIButton'

interface Props {
  row: CartDisplayRow
  /** Edit this item — opens the drawer in 'edit' mode. */
  onEdit: (itemId: string) => void
  /** Remove the item from the cart. */
  onRemove: (itemId: string) => void
  /** True while the configuration step is on THIS item's form, so the summary
   *  says which product the visible form belongs to. */
  isActive?: boolean
  /** Rendered inside the card under the add-ons — what a derived line is made of. */
  breakdownSlot?: React.ReactNode
  /** A line of a pack: neither edited nor removed on its own (the pack is removed as a whole). */
  locked?: boolean
}

/**
 * Single cart row. The card body (name / options) is clickable and opens
 * the product drawer in edit mode. The trash icon is an isolated click
 * target so the row-level handler doesn't swallow it.
 *
 * Quantity is display-only: for one-per-item products it follows the number
 * of uploaded photos (the drawer's EXACTLY_COUNT sync), and everything else
 * is one line per add — there is no manual stepper anywhere in the flow.
 *
 * M4 — remove is a single tap on the trash icon (no two-click confirm).
 *
 * `isActive` tints the card while the configuration step is showing that item's
 * form — with several configurable products in the cart the forms otherwise
 * give no clue which line they belong to.
 */
export const CartItemCard: React.FC<Props> = ({
  row,
  onEdit,
  onRemove,
  isActive = false,
  breakdownSlot,
  locked = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  // Rows always carry the list, but a hand-built row (tests, storyshots) may not.
  const addonLines = row.addonLines ?? []

  const priceLabel = row.lineTotal && row.currency
    ? formatPrice(row.lineTotal, row.currency)
    : t('purchase_flow_revamp:cart.price_unavailable')

  // What the line would have cost. Absent for every line with no saving, which is the
  // ordinary case, and the component then renders the charged price alone.
  const baseLabel = row.baseLineTotal && row.currency
    ? formatPrice(row.baseLineTotal, row.currency)
    : null

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRemove(row.id)
  }

  return (
    <Stack
      gap={0.8}
      data-cy={isActive ? 'pf-revamp-cart-item-active' : 'pf-revamp-cart-item'}
      sx={{
        // Same beige emphasis the sidebar uses for its property indicator, so
        // "this is the one you are looking at" reads as one visual language.
        backgroundColor: isActive ? BEIGE_500 : WHITE,
        border: `1px solid ${isActive ? BEIGE_700 : BEIGE_600}`,
        borderRadius: '0.8rem',
        padding: 1.2,
        transition: 'background-color 120ms',
        // The whole card opens the drawer, so it answers the pointer. A card
        // already tinted for being the one under configuration goes a step
        // deeper rather than nowhere.
        '&:hover': locked ? undefined : { backgroundColor: isActive ? BEIGE_600 : BEIGE_500 },
      }}
    >

      {/* Name, add-ons and the derived breakdown are one click target: every
          part of what was bought opens the same drawer. */}
      <Stack
        gap={0.4}
        {...(locked ? {} : pressableProps(() => onEdit(row.id)))}
        aria-label={locked ? undefined : t('purchase_flow_revamp:cart.edit_aria', { name: row.shellDisplayName })}
        sx={locked ? undefined : { cursor: 'pointer', borderRadius: '0.4rem', ...pressableFocusSx }}
      >

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={0.8}
      >

        <Stack gap={0.2} flex={1} minWidth={0}>

          <Stack direction="row" alignItems="center" gap={0.4}>

            <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
              {row.shellDisplayName}
            </Typography>

            {/* Beside the name, where it reads as part of what was bought
                rather than as a separate figure under it. */}
            <Typography variant="text-sm" fontWeight={550} color={GRAY_700} sx={{ flexShrink: 0 }}>
              {`\u00d7${row.quantity}`}
            </Typography>

            {row.hasAdvisoryExtras &&
              <MUITooltip content={t('purchase_flow_revamp:cart.advisory_tooltip')}>
                <InfoOutlinedIcon sx={{ fontSize: '1.4rem', color: GRAY_500 }} />
              </MUITooltip>
            }

          </Stack>

          {row.variantDisplayName &&
            <Typography variant="text-xs" color={GRAY_700}>
              {row.variantDisplayName}
            </Typography>
          }

        </Stack>

        <Typography variant="text-sm" fontWeight={550} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
          <StruckPrice price={priceLabel} base={baseLabel} variant="text-sm" free={Boolean(row.lineTotal?.eq(0) && row.baseLineTotal)} />
        </Typography>

      </Stack>

      {/* Named, not counted: "2 options" told the client nothing about what they
          had chosen. Outside the name column so the amounts line up under the
          line total rather than under the labels. */}
      {addonLines.length > 0 &&
        <Stack gap={0.2} data-cy="pf-revamp-cart-item-addons">
          {addonLines.map(({ label, amount }) => (
            <Stack key={label} direction="row" justifyContent="space-between" gap={0.8}>
              <Stack direction="row" gap={0.4}>
                <Typography variant="text-xs" color={GRAY_600}>+</Typography>
                <Typography variant="text-xs" color={GRAY_600}>{label}</Typography>
              </Stack>
              {amount && row.currency &&
                <Typography variant="text-xs" color={GRAY_600} whiteSpace="nowrap">
                  {formatPrice(amount, row.currency)}
                </Typography>
              }
            </Stack>
          ))}
        </Stack>
      }

      {breakdownSlot}

      </Stack>

      {!locked &&
        <Stack direction="row" alignItems="center" justifyContent="flex-end">

          {/* Labelled rather than icon-only, and hovering tints the icon AND the
              word instead of drawing a circle behind them — the button sits on a
              card that is itself tinted when active, so a second background
              would compete with that. */}
          <MUIButton
            type="secondaryNoBorder"
            onClick={handleRemoveClick}
            aria-label={t('purchase_flow_revamp:cart.remove_aria')}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: '2rem' }} />}
            sx={{
              minWidth: 0,
              padding: '0.4rem 0.4rem',
              color: GRAY_700,
              textTransform: 'none',
              '& .MuiButton-startIcon': { marginRight: '0.4rem', marginLeft: 0 },
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent', color: DARK_ORANGE },
            }}
          >
            <Typography variant="text-sm" fontWeight={550} color="inherit">
              {t('purchase_flow_revamp:cart.remove')}
            </Typography>
          </MUIButton>

        </Stack>
      }

    </Stack>
  )
}
